import { PALETTE, type PaletteColor } from '@hrms/shared'
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { LuChevronDown } from 'react-icons/lu'
import { cn } from '../../lib/cn.ts'

/** `md`: 34px control (`.ctl`). `sm`: 32px settings-grid input with radius 6. `xs`: 28px (filter bar). */
export type ControlSize = 'md' | 'sm' | 'xs'

function controlClass(size: ControlSize) {
  return cn(
    'w-full min-w-0 border border-border-strong bg-surface text-text placeholder:text-faint',
    'transition-colors hover:border-faint focus-visible:border-accent focus-visible:outline-none',
    'focus-visible:shadow-[0_0_0_3px_var(--accent-soft)] disabled:cursor-not-allowed disabled:opacity-60',
    'disabled:hover:border-border-strong',
    size === 'xs'
      ? 'h-7 rounded-input px-2 text-[13px]'
      : size === 'sm'
        ? 'h-8 rounded-input px-2.5 text-[13.5px]'
        : 'h-8.5 rounded-ctl px-2.5 text-sm',
  )
}

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize
  invalid?: boolean
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { size = 'md', invalid, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        controlClass(size),
        'read-only:bg-surface-2',
        invalid && 'border-danger hover:border-danger',
        className,
      )}
      {...rest}
    />
  )
})

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

/** Multi-line input with the modal form look (padding 8px 10px, accent focus ring). */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { invalid, className, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'block w-full min-w-0 resize-y rounded-ctl border border-border-strong bg-surface px-2.5 py-2 text-sm text-text placeholder:text-faint',
        'transition-colors hover:border-faint focus-visible:border-accent focus-visible:shadow-[0_0_0_3px_var(--accent-soft)] focus-visible:outline-none',
        invalid && 'border-danger hover:border-danger',
        className,
      )}
      {...rest}
    />
  )
})

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** React 19: passed straight through to the `<select>`. */
  ref?: Ref<HTMLSelectElement>
  size?: ControlSize
  /** Rendered before the select inside the control (e.g. a colour swatch). */
  leading?: ReactNode
  /** Classes for the wrapper; replaces the default `w-full` (so it can set its own width). */
  wrapperClassName?: string
}

export function Select({
  size = 'md',
  leading,
  className,
  wrapperClassName,
  children,
  ...rest
}: SelectProps) {
  return (
    <span className={cn('relative inline-flex min-w-0 items-center', wrapperClassName ?? 'w-full')}>
      {leading && (
        <span className="pointer-events-none absolute left-2.5 flex items-center">{leading}</span>
      )}
      <select
        className={cn(
          controlClass(size),
          'cursor-pointer appearance-none pr-7',
          leading ? 'pl-8' : undefined,
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <LuChevronDown
        aria-hidden
        className="pointer-events-none absolute right-2 size-4 text-muted"
      />
    </span>
  )
}

const COLOR_LABELS: Record<PaletteColor, string> = {
  slate: 'Slate',
  blue: 'Blue',
  violet: 'Violet',
  teal: 'Teal',
  green: 'Green',
  amber: 'Amber',
  orange: 'Orange',
  rose: 'Rose',
}

/** 14px square swatch in the palette colour (`--cf`). */
export function Swatch({ color, className }: { color: PaletteColor; className?: string }) {
  return (
    <span
      aria-hidden
      data-color={color}
      className={cn('inline-block size-3.5 shrink-0 rounded-lozenge bg-cf', className)}
    />
  )
}

export interface ColorSelectProps extends Omit<SelectProps, 'value' | 'onChange' | 'leading'> {
  value: PaletteColor
  onChange: (color: PaletteColor) => void
}

/** Swatch + select of the eight preset colours. */
export function ColorSelect({ value, onChange, ...rest }: ColorSelectProps) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as PaletteColor)}
      leading={<Swatch color={value} />}
      {...rest}
    >
      {PALETTE.map((c) => (
        <option key={c} value={c}>
          {COLOR_LABELS[c]}
        </option>
      ))}
    </Select>
  )
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode
}

/** Native checkbox with the accent colour; optional inline label. */
export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  const box = (
    <input
      type="checkbox"
      className={cn(
        'size-4 shrink-0 cursor-pointer accent-accent disabled:cursor-not-allowed disabled:opacity-50',
        !label && className,
      )}
      {...rest}
    />
  )
  if (!label) return box
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the input is `box`, rendered inside
    <label className={cn('inline-flex cursor-pointer items-center gap-2', className)}>
      {box}
      <span>{label}</span>
    </label>
  )
}
